import React from 'react';
import { Loader2, Paperclip, Plus, X } from 'lucide-react';
import {
  CHAT_CONTEXT_COST_HINT,
  formatAttachmentSize,
  isTimedMedia,
  type ChatAttachmentSummary,
  type ChatContextDocument,
} from '../services/chatAttachments';

/** Der Zähler der Leiste: wieviele Anhänge die Frage begleiten. */
function attachmentCount(count: number): string {
  return count === 1 ? '1 Anhang' : `${count} Anhänge`;
}

/**
 * Die Anhänge, die mit der nächsten Frage gehen. Jeder Chip nennt seinen
 * Dateinamen, seine Größe und einen Weg, ihn wieder zu entfernen; rechts
 * steht, wieviele es sind. Viele Anhänge bleiben in einer eigenen,
 * scrollbaren Fläche. Eine Datei, die nicht angenommen werden konnte, bleibt
 * stehen und sagt warum -- sie geht aber nicht mit.
 */
export const ChatAttachmentChips: React.FC<{
  attachments: ReadonlyArray<ChatAttachmentSummary>;
  /** Läuft gerade ein Upload, kommt ein wartender Chip dazu. */
  pendingCount?: number;
  disabled?: boolean;
  onRemove: (attachmentId: string) => void;
}> = ({ attachments, pendingCount = 0, disabled = false, onRemove }) => {
  if (attachments.length === 0 && pendingCount === 0) return null;
  return (
    <div className="mb-2 flex max-h-40 flex-wrap items-center gap-2 overflow-y-auto" data-testid="chat-attachment-chips">
      {attachments.map((attachment) => {
        const failed = attachment.status === 'failed';
        return (
          <span
            key={attachment.id}
            className={`inline-flex max-w-full items-center gap-2 border px-2 py-1 text-xs ${failed
              ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
              : 'border-warm-200 bg-warm-100 text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'}`}
            title={failed && attachment.errorMessage ? attachment.errorMessage : attachment.fileName}
          >
            <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{attachment.fileName}</span>
            <span className="shrink-0 text-muted dark:text-gray-400">{formatAttachmentSize(attachment.sizeBytes)}</span>
            {attachment.truncated && (
              <span className="shrink-0 text-muted dark:text-gray-400">gekürzt</span>
            )}
            <button
              type="button"
              onClick={() => onRemove(attachment.id)}
              disabled={disabled}
              aria-label={`Anhang ${attachment.fileName} entfernen`}
              className="shrink-0 text-muted transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-white"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {pendingCount > 0 && (
        <span className="inline-flex items-center gap-2 border border-warm-200 bg-warm-100 px-2 py-1 text-xs text-muted dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {pendingCount === 1 ? 'Eine Datei wird hochgeladen …' : `${pendingCount} Dateien werden hochgeladen …`}
        </span>
      )}
      <span className="ml-auto shrink-0 text-xs text-muted dark:text-gray-400">
        {attachmentCount(attachments.length + pendingCount)}
      </span>
    </div>
  );
};

/**
 * Die Dokumente im Kontext des Chats (AG-6): was eine frühere Frage schon
 * mitgenommen hat, geht mit jeder weiteren Frage erneut mit. Jedes steht als
 * Chip „Im Kontext: <Name>“ mit x zum Herausnehmen; Audio und Video sagen,
 * dass sie jede Frage erneut kosten. Ein herausgenommenes steht leiser da und
 * lässt sich mit + zurückholen. Eine Datei, die der Server nicht mehr hat,
 * geht nicht mit und bietet das erneute Hochladen an.
 */
export const ChatContextChips: React.FC<{
  documents: ReadonlyArray<ChatContextDocument>;
  disabled?: boolean;
  onToggle: (attachmentId: string, inContext: boolean) => void;
  onReupload: (attachmentId: string) => void;
}> = ({ documents, disabled = false, onToggle, onReupload }) => {
  if (documents.length === 0) return null;
  const chip = 'inline-flex max-w-full items-center gap-2 border px-2 py-1 text-xs';
  const button = 'shrink-0 text-muted transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-white';
  return (
    <div className="mb-2 flex max-h-40 flex-wrap items-center gap-2 overflow-y-auto" data-testid="chat-context-chips">
      {documents.map((document) => {
        if (!document.inContext) {
          return (
            <span key={document.id} className={`${chip} border-dashed border-warm-200 text-muted dark:border-gray-600 dark:text-gray-400`}
              title={document.fileName}>
              <span className="truncate">{document.fileName}</span>
              <button type="button" onClick={() => onToggle(document.id, true)} disabled={disabled}
                aria-label={`${document.fileName} wieder in den Kontext nehmen`} className={button}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          );
        }
        if (!document.available) {
          return (
            <span key={document.id} className={`${chip} border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200`}
              title={document.fileName}>
              <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{document.fileName} – nicht mehr verfügbar,</span>
              <button type="button" onClick={() => onReupload(document.id)} disabled={disabled}
                className="shrink-0 underline disabled:cursor-not-allowed disabled:opacity-50">
                erneut hochladen
              </button>
              <button type="button" onClick={() => onToggle(document.id, false)} disabled={disabled}
                aria-label={`${document.fileName} aus dem Kontext nehmen`} className={button}>
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          );
        }
        const timed = isTimedMedia(document.mimeType);
        return (
          <span key={document.id}
            className={`${chip} border-warm-200 bg-warm-100 text-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200`}
            title={timed ? `${document.fileName} – ${CHAT_CONTEXT_COST_HINT}` : document.fileName}>
            <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">Im Kontext: {document.fileName}</span>
            {timed && <span className="shrink-0 text-muted dark:text-gray-400">{CHAT_CONTEXT_COST_HINT}</span>}
            <button type="button" onClick={() => onToggle(document.id, false)} disabled={disabled}
              aria-label={`${document.fileName} aus dem Kontext nehmen`} className={button}>
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </span>
        );
      })}
    </div>
  );
};

/**
 * Die Anhänge an einer bereits gestellten Frage. Sie stehen unter ihrem Text
 * und tragen nur noch ihren Namen -- entfernen lässt sich hier nichts mehr.
 */
export const ChatAttachmentNames: React.FC<{
  attachments: ReadonlyArray<ChatAttachmentSummary>;
}> = ({ attachments }) => {
  if (attachments.length === 0) return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-2 border-t border-white/30 pt-2 text-xs" data-testid="chat-attachment-names">
      {attachments.map((attachment) => (
        <li key={attachment.id} className="inline-flex max-w-full items-center gap-1.5">
          <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{attachment.fileName}</span>
        </li>
      ))}
    </ul>
  );
};

export default ChatAttachmentChips;
